import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { GlobalStoreContextType } from "../context/GlobalStoreContext";
import { CalendarEvent } from "./CalendarEvent";
import { DropoffModel } from "./Dropoff.model";
import { MapObject } from "./MapObject";
import { RepeatingCalendarEventDefinition } from "./RepeatingCalendarEventDefinition";

dayjs.extend(isBetween);

export class Dropoff extends DropoffModel {
    static fromFeature(feature: GeoJSON.Feature, project: any, timezone: string): Dropoff {
        const def = project.repeating_calendar_event_definitions?.find(
            (def: any) => def.template_calendar_event?.name === "Project Hours"
        );

        const parsed = def ? RepeatingCalendarEventDefinition.fromApiObject(def, timezone) : null;

        return Dropoff.fromApiObject(
            {
                id: null,
                project_id: project.id,
                match_jobsite_hours: true,
                repeating_calendar_event_definitions: [
                    {
                        rrule: def?.rrule || "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR",
                        project_id: project.id,
                        template_calendar_event: {
                            project_id: project.id,
                            calendar_event_type: "availability",
                            name: "Hours",
                            start_at:
                                parsed?.templateCalendarEvent?.startAt ||
                                dayjs().tz(timezone).startOf("day").add(6, "hour"),
                            end_at:
                                parsed?.templateCalendarEvent?.endAt ||
                                dayjs().tz(timezone).startOf("day").add(18, "hour"),
                            is_template: true,
                        },
                    },
                ],
            },
            timezone
        );
    }

    // Mirrors the backend rule that a project always keeps an active dropoff for its deliveries.
    isLastActive(globalStoreContext: GlobalStoreContextType): boolean {
        return !globalStoreContext
            .dropoffsForProject(this.projectId)
            .some((dropoff) => dropoff.id !== this.id && !dropoff.archived);
    }

    getMapObject(globalStoreContext: GlobalStoreContextType): MapObject | null {
        const projectMapObjects = globalStoreContext.mapObjectsForProject(this.projectId || "");

        const mapObject = projectMapObjects.find(
            (mapObject) => mapObject.mappableType == "Dropoff" && mapObject.mappableId == this.id
        );

        if (mapObject) {
            return mapObject;
        }

        return null;
    }

    openHoursDefinition(): RepeatingCalendarEventDefinition | null {
        const defs: RepeatingCalendarEventDefinition[] = (this.repeatingCalendarEventDefinitions || []).map(
            (def: any) =>
                RepeatingCalendarEventDefinition.fromApiObject(def, this.project?.timezone || dayjs.tz.guess())
        );

        const hours = defs.find((def) => def.templateCalendarEvent?.calendarEventType === "availability");

        return hours || null;
    }

    openOnDay(checkDate: dayjs.Dayjs | null, closures?: CalendarEvent[]): boolean {
        const hours = this.openHoursDefinition();

        if (!hours) {
            return true;
        }

        if (!checkDate) {
            return true;
        }

        const checkDateStart = checkDate.startOf("day");
        const checkDateEnd = checkDate.endOf("day");

        if (hours.scheduleStart && checkDateEnd.isBefore(hours.scheduleStart)) {
            return false;
        }

        if (hours.scheduleEnd && checkDateStart.isAfter(hours.scheduleEnd)) {
            return false;
        }

        for (const closure of closures || []) {
            const closureStart = dayjs(closure.startAt).tz(this.project?.timezone || dayjs.tz.guess());
            const closureEnd = dayjs(closure.endAt).tz(this.project?.timezone || dayjs.tz.guess());

            const closed = checkDateStart.isBetween(closureStart, closureEnd, null, "[]");

            if (closed) {
                return false;
            }
        }

        return true;
    }
    nextClosure(closures: CalendarEvent[]): dayjs.Dayjs | null {
        const hours = this.openHoursDefinition();
        const hoursCloseDate = hours?.scheduleEnd ? dayjs(hours.scheduleEnd) : null;

        const nextClosure = closures
            .filter((closure) => dayjs(closure.startAt).isAfter(dayjs()))
            .sort((a, b) => dayjs(a.startAt).diff(dayjs(b.startAt)))[0];

        const nextClosureDate = nextClosure ? dayjs(nextClosure.startAt) : null;

        if (hoursCloseDate && nextClosureDate) {
            return hoursCloseDate.isBefore(nextClosureDate) ? hoursCloseDate : nextClosureDate;
        }

        return hoursCloseDate || nextClosureDate || null;
    }

    openDate(closures: CalendarEvent[]): string {
        const hours = this.openHoursDefinition();
        const scheduleStart = hours?.scheduleStart ? dayjs(hours.scheduleStart) : null;
        const scheduleEnd = hours?.scheduleEnd ? dayjs(hours.scheduleEnd) : null;

        const closuresHappeningNow = closures.filter(
            (closure) => dayjs(closure.startAt).isBefore(dayjs()) && dayjs(closure.endAt).isAfter(dayjs())
        );
        const soonestClosureEnd = closuresHappeningNow
            .map((closure) => dayjs(closure.endAt))
            .sort((a, b) => a.diff(b))[0];

        if (scheduleEnd?.isBefore(dayjs())) {
            return "Closed Until Further Notice";
        }

        if (scheduleStart && soonestClosureEnd) {
            return (
                "Opens: " +
                (scheduleStart.isAfter(soonestClosureEnd) ? scheduleStart : soonestClosureEnd).format("MM/DD/YYYY")
            );
        }

        if (soonestClosureEnd) {
            return "Opens: " + soonestClosureEnd.format("MM/DD/YYYY");
        }

        if (scheduleStart) {
            return "Opens: " + scheduleStart.format("MM/DD/YYYY");
        }

        return "";
    }
}
